import { Injectable } from '@angular/core';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { fetch } from '@ngrx/router-store/data-persistence';
import { map, switchMap, tap } from 'rxjs';

import { RouterFacade } from '@core/shared/data-access';
import { NotificationService, ToastDuration, ToastUrgency } from '@core/shared/util';
import { ArticleExport, ArticleExportStateType } from '@mp/content-manager/exports/domain';

import { FavoritesService } from '../services';

import { FavoritesActions } from './favorites.actions';

@Injectable()
export class FavoritesEffects {
  fetchFavoritesLists$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FavoritesActions.fetchFavoritesLists),
      fetch({
        run: () =>
          this.favoritesService
            .getAllLists()
            .pipe(map((favoritesLists) => FavoritesActions.fetchFavoritesListsSuccess({ favoritesLists }))),
        onError: () => {
          this.notificationService.toastDanger('Das Laden der Listen ist fehlgeschlagen. Bitte versuche es nochmal.');
          return FavoritesActions.fetchFavoritesListsError();
        },
      }),
    );
  });

  createFavoritesList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FavoritesActions.createFavoritesList),
      fetch({
        id: ({ createDto }) => createDto.name,
        run: ({ createDto }) =>
          this.favoritesService.createList(createDto).pipe(
            map((createdList) => FavoritesActions.createFavoritesListSuccess({ createdList })),
            tap(() => this.notificationService.toastSuccess('Die Liste wurde erfolgreich erstellt.')),
          ),
        onError: ({ createDto }) => {
          this.notificationService.toastCustom(
            'Das Erstellen der Liste ist fehlgeschlagen. Bitte prüfe deine Eingabe und versuche es nochmal.',
            ToastDuration.Long,
            ToastUrgency.Danger,
          );
          return FavoritesActions.createFavoritesListError({ listName: createDto.name });
        },
      }),
    );
  });

  updateFavoritesList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FavoritesActions.updateFavoritesList),
      fetch({
        id: ({ listId }) => listId,
        run: ({ listId, updateDto }) => {
          return this.favoritesService.updateList(listId, updateDto).pipe(
            map((updatedList) => FavoritesActions.updateFavoritesListSuccess({ updatedList })),
            tap(() => this.notificationService.toastSuccess('Die Änderungen wurden gespeichert.')),
          );
        },
        onError: () => {
          this.notificationService.toastCustom(
            'Das Speichern der aktualisierten Liste ist fehlgeschlagen. Bitte prüfe deine Eingabe und versuche es nochmal.',
            ToastDuration.Long,
            ToastUrgency.Danger,
          );
          return FavoritesActions.updateFavoritesListError();
        },
      }),
    );
  });

  deleteFavoritesList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FavoritesActions.deleteFavoritesList),
      fetch({
        id: ({ listId }) => listId,
        run: ({ listId }) => {
          return this.favoritesService.deleteList(listId).pipe(
            map(() => FavoritesActions.deleteFavoritesListSuccess({ listId })),
            tap(() => this.notificationService.toastSuccess('Die Liste wurde erfolgreich gelöscht.')),
          );
        },
        onError: () => {
          this.notificationService.toastDanger('Das Löschen der Liste ist fehlgeschlagen. Bitte versuche es nochmal.');
          return FavoritesActions.deleteFavoritesListError();
        },
      }),
    );
  });

  exportFavoritesList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FavoritesActions.exportFavoritesList),
      fetch({
        id: ({ listId }) => listId,
        run: ({ listId, exportType }) =>
          this.favoritesService
            .export(listId, exportType)
            .pipe(map((articleExport) => FavoritesActions.exportFavoritesListSuccess({ articleExport }))),
        onError: () => FavoritesActions.exportFavoritesListError(),
      }),
    );
  });

  exportFavoritesListSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(FavoritesActions.exportFavoritesListSuccess),
        switchMap(({ articleExport }) =>
          this.createExportSuccessInfoToast(articleExport)
            .onAction()
            .pipe(tap(() => this.navigateToExportHistory(articleExport.exportType))),
        ),
      );
    },
    { dispatch: false },
  );

  constructor(
    private readonly actions$: Actions,
    private readonly favoritesService: FavoritesService,
    private readonly notificationService: NotificationService,
    private readonly routerFacade: RouterFacade,
  ) {}

  private createExportSuccessInfoToast(articleExport: ArticleExport): MatSnackBarRef<SimpleSnackBar> {
    const exportMessage =
      articleExport.state.type === ArticleExportStateType.Pending && articleExport.state.jobId == null
        ? 'Der Export deiner Favoritenliste wurde angelegt und kann weiter verarbeitet werden.'
        : 'Der Download deiner Favoritenliste wurde gestartet.';

    return this.notificationService.toastCustom(
      exportMessage,
      ToastDuration.Long,
      ToastUrgency.Info,
      'Export Historie',
    );
  }

  private navigateToExportHistory(exportType: string): void {
    this.routerFacade.navigate((router) =>
      router.navigate(['/', 'artikel', 'exports'], {
        queryParams: { exportTypes: exportType },
      }),
    );
  }
}
